/**
 * Structured data for the Jourdanton ISD School steel stitch walkway awning
 * re-cover (before / after). ImageObject entries give Google Images explicit
 * captions, licensing and creator info so the photos can be indexed and
 * attributed to Texas Made Shade instead of being treated as decoration.
 */
import { canonicalUrl, SITE_URL, SITE_NAME, absUrl } from "@/lib/seo";

export interface AwningBeforeAfterInput {
  /** Page path the images live on, e.g. "/awnings" */
  path: string;
  /** Absolute or site-relative URL of the "before" (faded cover) photo */
  beforeUrl: string;
  /** Absolute or site-relative URL of the "after" (new cover) photo */
  afterUrl: string;
}

export function awningBeforeAfterScripts(input: AwningBeforeAfterInput) {
  const pageUrl = canonicalUrl(input.path);
  const creator = { "@type": "Organization", name: SITE_NAME, url: `${SITE_URL}/` };
  const copyrightNotice = `© 2026 ${SITE_NAME}. All rights reserved.`;

  const before = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "@id": `${pageUrl}#image-steel-stitch-before`,
    contentUrl: absUrl(input.beforeUrl),
    url: absUrl(input.beforeUrl),
    name: "Before — Steel Stitch Walkway Awning Re-Cover, Jourdanton ISD School",
    alternateName: "Faded red steel stitch walkway awning cover before replacement",
    caption:
      "Before: sun-faded red steel stitch walkway awning cover at Jourdanton ISD School in Texas, photographed by Texas Made Shade prior to a marine-grade canvas re-cover.",
    description:
      "Original faded canvas top on a steel stitch tube-frame walkway awning at Jourdanton ISD School. The frame remained structurally sound, so Texas Made Shade re-covered it with new marine-grade Sunbrella® canvas rather than replacing the whole structure.",
    representativeOfPage: false,
    creator,
    copyrightHolder: creator,
    copyrightNotice,
    creditText: SITE_NAME,
    license: `${SITE_URL}/`,
    acquireLicensePage: `${SITE_URL}/contact`,
    contentLocation: { "@type": "Place", name: "Jourdanton ISD School, Jourdanton, Texas" },
    keywords:
      "steel stitch awning re-cover, faded awning cover replacement, school walkway awning Texas, commercial awning recover San Antonio",
    isPartOf: { "@id": `${pageUrl}#gallery` },
  };

  const after = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "@id": `${pageUrl}#image-steel-stitch-after`,
    contentUrl: absUrl(input.afterUrl),
    url: absUrl(input.afterUrl),
    name: "After — New Burgundy Canvas Cover, Jourdanton ISD School Walkway Awning",
    alternateName: "Steel stitch walkway awning with a brand-new burgundy canvas cover",
    caption:
      "After: the same Jourdanton ISD School steel stitch walkway awning with a brand-new burgundy marine-grade canvas cover fabricated and installed by Texas Made Shade.",
    description:
      "Completed steel stitch walkway awning re-cover at Jourdanton ISD School — new burgundy marine-grade canvas patterned, sewn and installed by Texas Made Shade in Seguin, Texas.",
    representativeOfPage: true,
    creator,
    copyrightHolder: creator,
    copyrightNotice,
    creditText: SITE_NAME,
    license: `${SITE_URL}/`,
    acquireLicensePage: `${SITE_URL}/contact`,
    contentLocation: { "@type": "Place", name: "Jourdanton ISD School, Jourdanton, Texas" },
    keywords:
      "new awning canvas cover, steel stitch awning, commercial walkway awning Texas, custom awning re-cover Jourdanton",
    isPartOf: { "@id": `${pageUrl}#gallery` },
  };

  const gallery = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "@id": `${pageUrl}#gallery`,
    name: "Custom Awning Before & After — Steel Stitch Walkway Re-Cover",
    description:
      "Before and after photos of a steel stitch walkway awning re-cover at Jourdanton ISD School by Texas Made Shade.",
    url: pageUrl,
    associatedMedia: [
      { "@id": `${pageUrl}#image-steel-stitch-before` },
      { "@id": `${pageUrl}#image-steel-stitch-after` },
    ],
  };

  return [
    { type: "application/ld+json", children: JSON.stringify(before) },
    { type: "application/ld+json", children: JSON.stringify(after) },
    { type: "application/ld+json", children: JSON.stringify(gallery) },
  ];
}
